import { useHistory } from 'react-router-dom';
import usePagination from '../../../Pagination/hooks/usePagination';
import {
  CUSTOM_ROLES_N_PERMISSIONS_PER_PAGE,
  PERMISSIONS,
} from '../../../../utils/constants';
import { PAGINATION_STATUS } from '../../../Pagination/utils/PageStatus';
import {
  AllCustomRoleResponse,
  CustomRoleResponse,
  GET_CUSTOM_ROLES_N_PERMISSIONS,
} from '../../../../apollo/queries/v3/RolesNPermissions/getAllCustomRolesNPermissions.queries';
import { Role } from '../../types';
import { sortByGroupName } from '../../utils/sortByGroupName';
import { ROUTE_RNP } from '../../../Routing/routeHelpers';
import useDeleteCustomRoleAPI from '../../CreateNewRole/hooks/useDeleteCustomRoleAPI';
import appLocalStorage from '../../../../utils/AppLocalStorage';

const useCustomRoles = () => {
  const {
    queryData: { loading, error, data, refetch: refetchCustomRoles },
    pageData: { currentPage, totalPages, pageStats },
    actions: { setCurrentPage },
  } = usePagination<AllCustomRoleResponse>({
    query: GET_CUSTOM_ROLES_N_PERMISSIONS,
    responseField: 'allCustomRoles',
    pageSize: CUSTOM_ROLES_N_PERMISSIONS_PER_PAGE,
    pageStatusFormat: `${PAGINATION_STATUS.FROM}-${PAGINATION_STATUS.TO} of ${PAGINATION_STATUS.TOTAL} items`,
  });
  const history = useHistory();

  const getResponseRoles = () =>
    data && data.allCustomRoles && data.allCustomRoles.roles;

  const getRoles = (): Role[] => {
    const responseRoles = getResponseRoles();

    if (responseRoles) {
      return responseRoles.map((role: CustomRoleResponse) => ({
        id: role.id,
        name: role.name,
        permissions: `${role.permissions.length} ${PERMISSIONS}`,
        modules: [
          {
            id: role.organisation.code,
            name: role.organisation.name,
          },
        ],
        descriptionText: role.description,
        permissionsText: `${sortByGroupName(role.permissions).join(', ')}.`,
      }));
    }

    return [];
  };

  const onEdit = (roleId: string) => {
    const route = ROUTE_RNP.updateCustomRole.replace(':roleId', roleId);
    history.push(route);
  };

  const [deleteCustomRole] = useDeleteCustomRoleAPI({
    onComplete: () => {
      refetchCustomRoles();
    },
  });

  return {
    rolesResponse: {
      isLoading: loading,
      error,
      currentPage,
      totalCount:
        data && data.allCustomRoles ? data.allCustomRoles.totalCount : 0,
      roles: getRoles(),
      totalPages,
    },
    isViewOnlyRole: appLocalStorage.isViewOnlyRole(),
    setCurrentPage,
    pageStats,
    onEdit,
    onDelete: deleteCustomRole,
  };
};

export default useCustomRoles;
